import {
  INSPECTION_PDF_LAYOUT_LABELS,
  isAnswerDefect,
  type InspectionAnswerValue,
  type InspectionPdfLayout,
  type InspectionQuestion,
  type InspectionSchema,
} from '@sitewise/shared';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  ErrorState,
  Input,
  Label,
  Skeleton,
  toast,
} from '@sitewise/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  ArrowLeft,
  Camera,
  CheckCircle2,
  Download,
  FileText,
  Play,
  RefreshCcw,
  Send,
  X,
} from 'lucide-react';
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { fetchMe } from '../../../api/auth.js';
import { fetchBuilding, listUnits } from '../../../api/buildings.js';
import {
  acknowledgeInspectionDefect,
  cancelInspection,
  confirmAnswerAttachment,
  createWorkOrdersFromDefects,
  fetchInspection,
  fetchInspectionPdf,
  previewInspectionPdf,
  presignAnswerAttachment,
  retryInspectionPdf,
  startInspection,
  submitInspection,
  upsertInspectionAnswer,
  type InspectionAnswerSummary,
  type InspectionDetail,
} from '../../../api/inspections.js';
import { listOrgUsers } from '../../../api/org.js';
import { ApiError } from '../../../lib/api-client.js';
import {
  saveAnswerOfflineAware,
  submitInspectionOfflineAware,
} from '../../../offline/inspection-runner.js';
import { useOfflineContext } from '../../../offline/use-offline-context.js';

import { ChecklistTableSection } from './checklist-table-section.js';
import {
  isChecklistSection,
  resolvePdfLayouts,
  type RunnerViewMode,
} from './form-helpers.js';
import { formatDateTime, StatusBadge } from './lib.js';
import { SignatureField } from './signature-field.js';
import {
  buildPrefillContext,
  useFooterFieldSync,
  useInspectionPrefill,
} from './use-inspection-form-sync.js';

export function InspectionDetailPage() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const [runnerView, setRunnerView] = useState<RunnerViewMode>('table');
  const [submitWoPromptOpen, setSubmitWoPromptOpen] = useState(false);
  const [checklistNotes, setChecklistNotes] = useState<Record<string, string>>({});

  const detailQuery = useQuery({
    queryKey: ['inspection', id],
    queryFn: () => fetchInspection(id!),
    enabled: Boolean(id),
  });

  const buildingId = detailQuery.data?.inspection.buildingId;
  const unitId = detailQuery.data?.inspection.unitId ?? null;

  const buildingQuery = useQuery({
    queryKey: ['building', buildingId],
    queryFn: () => fetchBuilding(buildingId!),
    enabled: Boolean(buildingId),
  });

  const unitsQuery = useQuery({
    queryKey: ['building', buildingId, 'units'],
    queryFn: () => listUnits(buildingId!),
    enabled: Boolean(buildingId && unitId),
  });

  const usersQuery = useQuery({
    queryKey: ['org', 'users'],
    queryFn: listOrgUsers,
  });

  const meQuery = useQuery({ queryKey: ['me'], queryFn: fetchMe });

  const startMut = useMutation({
    mutationFn: () => startInspection(id!),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['inspection', id] }),
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Could not start'),
  });

  const cancelMut = useMutation({
    mutationFn: (reason: string) => cancelInspection(id!, reason),
    onSuccess: () => {
      toast.success('Inspection cancelled.');
      void queryClient.invalidateQueries({ queryKey: ['inspection', id] });
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Could not cancel'),
  });

  const offlineCtx = useOfflineContext();

  useEffect(() => {
    if (!detailQuery.data) return;
    setChecklistNotes((prev) => {
      const next = { ...prev };
      for (const answer of detailQuery.data.answers) {
        if (answer.notes) next[answer.questionId] = answer.notes;
      }
      return next;
    });
  }, [detailQuery.data]);

  const prefillCtx = useMemo(() => {
    const inspection = detailQuery.data?.inspection;
    const unitNumber =
      unitsQuery.data?.units.find((u) => u.id === unitId)?.unitNumber ?? null;
    return buildPrefillContext({
      building: buildingQuery.data?.building ?? null,
      unitNumber,
      inspectorName: meQuery.data?.user.name ?? null,
      inspectionDateIso: inspection?.startedAt ?? inspection?.scheduledFor ?? null,
    });
  }, [buildingQuery.data, unitsQuery.data, unitId, meQuery.data, detailQuery.data]);

  useInspectionPrefill({
    enabled: detailQuery.data?.inspection.status === 'in_progress',
    inspectionId: id!,
    schema: (detailQuery.data?.templateVersion.schema as InspectionSchema) ?? { sections: [] },
    answers: detailQuery.data?.answers ?? [],
    ctx: prefillCtx,
  });

  useFooterFieldSync({
    enabled: detailQuery.data?.inspection.status === 'in_progress',
    inspectionId: id!,
    schema: (detailQuery.data?.templateVersion.schema as InspectionSchema) ?? { sections: [] },
    answers: detailQuery.data?.answers ?? [],
    onSynced: () => void queryClient.invalidateQueries({ queryKey: ['inspection', id] }),
  });

  const submitMut = useMutation({
    mutationFn: async () => {
      if (offlineCtx.userId && offlineCtx.orgSlug) {
        const result = await submitInspectionOfflineAware({
          userId: offlineCtx.userId,
          orgSlug: offlineCtx.orgSlug,
          inspectionId: id!,
          input: { notes: null },
        });
        if (result.queued) {
          toast.success('Submit queued — will sync when reconnected');
          return null;
        }
        return result.inspection;
      }
      return submitInspection(id!);
    },
    onSuccess: () => {
      toast.success('Inspection submitted. PDF rendering in the background.');
      void queryClient.invalidateQueries({ queryKey: ['inspection', id] });
    },
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.message : 'Could not submit inspection'),
  });

  if (detailQuery.isError) return <ErrorState description="Could not load inspection." />;
  if (detailQuery.isLoading || !detailQuery.data) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-10 w-72" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  const detail = detailQuery.data;
  const { inspection, template, templateVersion, answers } = detail;
  const schema = templateVersion.schema as InspectionSchema;
  const isChecklistForm = schema.layout === 'checklist_table';
  const pdfLayouts = resolvePdfLayouts(schema);
  const orgUsers = usersQuery.data?.users ?? [];

  const isReadOnly = inspection.status !== 'in_progress';

  const answersById = new Map(answers.map((a) => [a.questionId, a]));
  const totalQuestions = schema.sections.reduce(
    (acc, sec) => acc + sec.questions.filter((q) => q.kind !== 'section_header').length,
    0,
  );
  const answeredCount = answers.filter((a) => a.value !== null).length;

  const unresolvedDefects = answers.filter(
    (a) => a.isDefect && !a.defectResolvedByWorkOrderId && !a.isDefectAcknowledged,
  );

  function handleSubmitClick() {
    if (unresolvedDefects.length > 0) {
      setSubmitWoPromptOpen(true);
      return;
    }
    submitMut.mutate();
  }

  function commitChecklistAnswer(
    question: Extract<InspectionQuestion, { kind: 'yes_no' }>,
    value: 'yes' | 'no' | 'na',
    notes: string,
  ) {
    if (isReadOnly) return;
    void upsertInspectionAnswer(inspection.id, question.id, {
      value: { kind: 'yes_no', value },
      notes: notes.trim() || null,
      photoAttachmentIds: answersById.get(question.id)?.photoAttachmentIds ?? [],
    }).then(() => queryClient.invalidateQueries({ queryKey: ['inspection', id] }));
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Button asChild variant="ghost" size="icon" aria-label="Back">
          <Link to="/inspections/runs">
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h1 className="truncate text-xl font-semibold">{template.name}</h1>
            <StatusBadge status={inspection.status} />
          </div>
          <div className="text-xs text-fg-muted">
            Inspection {inspection.id.slice(0, 8)} · scheduled{' '}
            {formatDateTime(inspection.scheduledFor)}
            {inspection.startedAt ? ` · started ${formatDateTime(inspection.startedAt)}` : ''}
            {inspection.submittedAt
              ? ` · submitted ${formatDateTime(inspection.submittedAt)}`
              : ''}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {inspection.status === 'scheduled' ? (
            <Button onClick={() => startMut.mutate()} disabled={startMut.isPending}>
              <Play className="h-4 w-4" />
              {startMut.isPending ? 'Starting…' : 'Start'}
            </Button>
          ) : null}
          {inspection.status === 'in_progress' ? (
            <Button
              onClick={handleSubmitClick}
              disabled={submitMut.isPending}
              variant="default"
            >
              <Send className="h-4 w-4" />
              {submitMut.isPending ? 'Submitting…' : 'Submit'}
            </Button>
          ) : null}
          {inspection.status === 'submitted' ? (
            <PdfButton inspectionId={inspection.id} pdfLayouts={pdfLayouts} />
          ) : pdfLayouts.length > 1 || pdfLayouts[0] !== 'default' ? (
            <PdfButton
              inspectionId={inspection.id}
              pdfLayouts={pdfLayouts}
              previewOnly
            />
          ) : null}
          {inspection.status !== 'submitted' && inspection.status !== 'cancelled' ? (
            <Button
              variant="ghost"
              onClick={() => {
                const reason = prompt('Cancel reason (optional):') ?? '';
                cancelMut.mutate(reason);
              }}
            >
              <X className="h-4 w-4" />
              Cancel
            </Button>
          ) : null}
        </div>
      </div>

      {!isReadOnly ? (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded border border-border bg-surface px-3 py-2 text-sm">
          <div>
            <span className="font-medium">{answeredCount}</span> of{' '}
            <span className="font-medium">{totalQuestions}</span> questions answered.
          </div>
          {isChecklistForm ? (
            <div className="flex gap-1 rounded border border-border p-0.5">
              <Button
                type="button"
                size="sm"
                variant={runnerView === 'table' ? 'default' : 'ghost'}
                onClick={() => setRunnerView('table')}
              >
                Table view
              </Button>
              <Button
                type="button"
                size="sm"
                variant={runnerView === 'card' ? 'default' : 'ghost'}
                onClick={() => setRunnerView('card')}
              >
                Card view
              </Button>
            </div>
          ) : null}
        </div>
      ) : null}

      <div className="space-y-4">
        {schema.sections.map((section) => {
          const useTable =
            runnerView === 'table' && isChecklistForm && isChecklistSection(section.title);
          return (
            <Card key={section.id}>
              <CardHeader>
                <CardTitle className="text-base">{section.title}</CardTitle>
                {section.description ? (
                  <p className="text-sm text-fg-muted">{section.description}</p>
                ) : null}
              </CardHeader>
              <CardContent className="space-y-4">
                {useTable ? (
                  <ChecklistTableSection
                    title={section.title}
                    questions={section.questions}
                    answersById={answersById}
                    readOnly={isReadOnly}
                    localNotes={checklistNotes}
                    onNotesChange={(questionId, notes) =>
                      setChecklistNotes((prev) => ({ ...prev, [questionId]: notes }))
                    }
                    onCommit={commitChecklistAnswer}
                  />
                ) : (
                  section.questions.map((q) => (
                    <QuestionRow
                      key={q.id}
                      question={q}
                      answer={answersById.get(q.id) ?? null}
                      inspectionId={inspection.id}
                      readOnly={isReadOnly}
                      orgUsers={orgUsers}
                      hideInlineNotes={
                        isChecklistForm &&
                        isChecklistSection(section.title) &&
                        q.kind === 'yes_no'
                      }
                    />
                  ))
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {inspection.status === 'in_progress' && unresolvedDefects.length > 0 ? (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-2">
            <CardTitle className="text-base">
              Deficiencies ({unresolvedDefects.length})
            </CardTitle>
            <Button onClick={() => setSubmitWoPromptOpen(true)}>
              <FileText className="h-4 w-4" />
              Create work orders
            </Button>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-fg-muted">
              &ldquo;No&rdquo; answers are flagged as deficiencies. Create work orders before
              submitting, or submit and handle them afterward.
            </p>
          </CardContent>
        </Card>
      ) : null}

      {submitWoPromptOpen ? (
        <SubmitWithDefectsDialog
          detail={detail}
          onClose={() => setSubmitWoPromptOpen(false)}
          onSubmitAnyway={() => {
            setSubmitWoPromptOpen(false);
            submitMut.mutate();
          }}
        />
      ) : null}

      {inspection.status === 'submitted' ? <DefectReview detail={detail} /> : null}
    </div>
  );
}

function QuestionRow({
  question,
  answer,
  inspectionId,
  readOnly,
  orgUsers,
  hideInlineNotes = false,
}: {
  question: InspectionQuestion;
  answer: InspectionAnswerSummary | null;
  inspectionId: string;
  readOnly: boolean;
  orgUsers: Array<{ id: string; name: string }>;
  hideInlineNotes?: boolean;
}) {
  const queryClient = useQueryClient();
  const offlineCtx = useOfflineContext();
  const [localValue, setLocalValue] = useState<InspectionAnswerValue | null>(
    (answer?.value as InspectionAnswerValue | null) ?? null,
  );
  const [localNotes, setLocalNotes] = useState<string>(answer?.notes ?? '');
  const [photos, setPhotos] = useState<string[]>(answer?.photoAttachmentIds ?? []);
  const [signatureUploading, setSignatureUploading] = useState(false);
  const [signaturePrintedName, setSignaturePrintedName] = useState(() => {
    const v = answer?.value;
    return v?.kind === 'signature' ? v.value?.printedName ?? '' : '';
  });

  const upsert = useMutation({
    mutationFn: async (input: {
      value: InspectionAnswerValue | null;
      notes: string | null;
      photoAttachmentIds: string[];
    }) => {
      const payload = {
        value: input.value as never,
        notes: input.notes,
        photoAttachmentIds: input.photoAttachmentIds,
      };
      if (offlineCtx.userId && offlineCtx.orgSlug) {
        const result = await saveAnswerOfflineAware({
          userId: offlineCtx.userId,
          orgSlug: offlineCtx.orgSlug,
          inspectionId,
          questionId: question.id,
          input: payload,
        });
        if (result.queued) {
          toast.success('Saved locally — will sync when reconnected');
        }
        return result;
      }
      return upsertInspectionAnswer(inspectionId, question.id, payload);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['inspection', inspectionId] }),
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.message : 'Could not save answer'),
  });

  function commit(
    value: InspectionAnswerValue | null = localValue,
    notes = localNotes,
    attachments = photos,
  ) {
    if (readOnly) return;
    setLocalValue(value);
    setLocalNotes(notes);
    setPhotos(attachments);
    upsert.mutate({ value, notes: notes || null, photoAttachmentIds: attachments });
  }

  async function uploadSignature(blob: Blob, printedName: string) {
    setSignatureUploading(true);
    try {
      const presign = await presignAnswerAttachment(inspectionId, {
        questionId: question.id,
        kind: 'inspection_signature',
        filename: 'signature.png',
        contentType: 'image/png',
        sizeBytes: blob.size,
      });
      await fetch(presign.uploadUrl, {
        method: 'PUT',
        body: blob,
        headers: presign.headers,
      });
      await confirmAnswerAttachment(inspectionId, question.id, presign.attachmentId);
      commit(
        {
          kind: 'signature',
          value: {
            attachmentId: presign.attachmentId,
            printedName: printedName.trim() || null,
          },
        },
        localNotes,
        photos,
      );
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not save signature');
    } finally {
      setSignatureUploading(false);
    }
  }

  const isDefect = useMemo(() => {
    if (!localValue) return false;
    return isAnswerDefect(question, localValue);
  }, [question, localValue]);

  if (question.kind === 'section_header') {
    return (
      <div className="border-l-2 border-primary pl-3 text-sm font-semibold uppercase tracking-wide text-fg-secondary">
        {question.label}
      </div>
    );
  }

  const required = (question as { required?: boolean }).required ?? false;
  const helpText = (question as { helpText?: string }).helpText;

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Label className="text-sm font-medium">
          {(question as { label: string }).label}
          {required ? <span className="ml-1 text-danger">*</span> : null}
        </Label>
        {isDefect ? (
          <Badge variant="danger" className="text-[10px]">
            <AlertTriangle className="h-3 w-3" />
            DEFECT
          </Badge>
        ) : null}
      </div>
      {helpText ? <p className="text-xs text-fg-muted">{helpText}</p> : null}

      <AnswerInput
        question={question}
        value={localValue}
        onChange={(v) => commit(v)}
        readOnly={readOnly}
        orgUsers={orgUsers}
        signatureUploading={signatureUploading}
        onSignatureCapture={
          question.kind === 'signature'
            ? (blob) => uploadSignature(blob, signaturePrintedName)
            : undefined
        }
        onPrintedNameChange={
          question.kind === 'signature' && question.collectPrintedName
            ? (printedName) => {
                setSignaturePrintedName(printedName);
                const current = localValue?.kind === 'signature' ? localValue.value : null;
                if (current?.attachmentId) {
                  commit(
                    {
                      kind: 'signature',
                      value: {
                        ...current,
                        printedName: printedName.trim() || null,
                      },
                    },
                    localNotes,
                    photos,
                  );
                }
              }
            : undefined
        }
        signaturePrintedName={signaturePrintedName}
      />

      {!hideInlineNotes && (!readOnly || localNotes) ? (
        <textarea
          rows={question.kind === 'long_text' ? 6 : 2}
          placeholder={
            question.questionKey === 'footer.comments'
              ? 'Comments (include housekeeping)'
              : 'Notes (optional)'
          }
          className="w-full rounded border border-border bg-surface px-3 py-2 text-sm"
          value={localNotes}
          onChange={(e) => setLocalNotes(e.target.value)}
          onBlur={() => commit(localValue, localNotes, photos)}
          disabled={readOnly}
        />
      ) : null}

      <PhotoStrip
        inspectionId={inspectionId}
        questionId={question.id}
        attachmentIds={photos}
        onChange={(next) => commit(localValue, localNotes, next)}
        readOnly={readOnly}
      />
    </div>
  );
}

function AnswerInput({
  question,
  value,
  onChange,
  readOnly,
  orgUsers,
  signatureUploading,
  onSignatureCapture,
  onPrintedNameChange,
  signaturePrintedName = '',
}: {
  question: InspectionQuestion;
  value: InspectionAnswerValue | null;
  onChange: (next: InspectionAnswerValue | null) => void;
  readOnly: boolean;
  orgUsers: Array<{ id: string; name: string }>;
  signatureUploading?: boolean;
  onSignatureCapture?: (blob: Blob) => Promise<void>;
  onPrintedNameChange?: (name: string) => void;
  signaturePrintedName?: string;
}) {
  switch (question.kind) {
    case 'yes_no': {
      const v = value?.kind === 'yes_no' ? value.value : null;
      const options: Array<'yes' | 'no' | 'na'> = question.allowNa
        ? ['na', 'yes', 'no']
        : ['yes', 'no'];
      return (
        <div className="flex flex-wrap gap-2">
          {options.map((opt) => (
            <Button
              key={opt}
              type="button"
              variant={v === opt ? 'default' : 'outline'}
              disabled={readOnly}
              onClick={() => onChange({ kind: 'yes_no', value: opt })}
            >
              {opt === 'yes' ? 'Yes' : opt === 'no' ? 'No' : 'N/A'}
            </Button>
          ))}
        </div>
      );
    }
    case 'pass_fail': {
      const v = value?.kind === 'pass_fail' ? value.value : null;
      return (
        <div className="flex gap-2">
          <Button
            type="button"
            variant={v === 'pass' ? 'default' : 'outline'}
            disabled={readOnly}
            onClick={() => onChange({ kind: 'pass_fail', value: 'pass' })}
          >
            <CheckCircle2 className="h-4 w-4" />
            Pass
          </Button>
          <Button
            type="button"
            variant={v === 'fail' ? 'destructive' : 'outline'}
            disabled={readOnly}
            onClick={() => onChange({ kind: 'pass_fail', value: 'fail' })}
          >
            <X className="h-4 w-4" />
            Fail
          </Button>
        </div>
      );
    }
    case 'rating': {
      const v = value?.kind === 'rating' ? value.value : null;
      return (
        <div className="flex gap-2">
          {Array.from({ length: question.scale }).map((_, i) => {
            const n = i + 1;
            return (
              <Button
                key={n}
                type="button"
                variant={v === n ? 'default' : 'outline'}
                size="icon"
                disabled={readOnly}
                onClick={() => onChange({ kind: 'rating', value: n })}
              >
                {n}
              </Button>
            );
          })}
        </div>
      );
    }
    case 'short_text':
      return (
        <Input
          value={value?.kind === 'short_text' ? value.value ?? '' : ''}
          onChange={(e) => onChange({ kind: 'short_text', value: e.target.value || null })}
          disabled={readOnly}
        />
      );
    case 'long_text':
      return (
        <textarea
          rows={4}
          className="w-full rounded border border-border bg-surface px-3 py-2 text-sm"
          value={value?.kind === 'long_text' ? value.value ?? '' : ''}
          onChange={(e) => onChange({ kind: 'long_text', value: e.target.value || null })}
          disabled={readOnly}
        />
      );
    case 'number':
      return (
        <div className="flex items-center gap-2">
          <Input
            type="number"
            value={value?.kind === 'number' && value.value !== null ? value.value : ''}
            onChange={(e) =>
              onChange({
                kind: 'number',
                value: e.target.value === '' ? null : Number(e.target.value),
              })
            }
            disabled={readOnly}
          />
          {question.unit ? <span className="text-sm text-fg-muted">{question.unit}</span> : null}
        </div>
      );
    case 'single_select': {
      const v = value?.kind === 'single_select' ? value.value : null;
      const dynamicOptions =
        question.optionSource === 'org_users'
          ? orgUsers.map((user) => ({ value: user.id, label: user.name }))
          : [];
      const options = [...question.options, ...dynamicOptions];
      if (options.length > 4 || question.optionSource === 'org_users') {
        return (
          <select
            className="w-full rounded border border-border bg-surface px-3 py-2 text-sm"
            value={v ?? ''}
            disabled={readOnly}
            onChange={(e) =>
              onChange({
                kind: 'single_select',
                value: e.target.value || null,
              })
            }
          >
            <option value="">Select…</option>
            {options.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        );
      }
      return (
        <div className="flex flex-wrap gap-2">
          {options.map((opt) => (
            <Button
              key={opt.value}
              type="button"
              variant={v === opt.value ? 'default' : 'outline'}
              disabled={readOnly}
              onClick={() => onChange({ kind: 'single_select', value: opt.value })}
            >
              {opt.label}
            </Button>
          ))}
        </div>
      );
    }
    case 'multi_select': {
      const v = value?.kind === 'multi_select' ? value.value : [];
      function toggle(option: string) {
        const has = v.includes(option);
        const next = has ? v.filter((x) => x !== option) : [...v, option];
        onChange({ kind: 'multi_select', value: next });
      }
      return (
        <div className="flex flex-wrap gap-2">
          {question.options.map((opt) => (
            <Button
              key={opt.value}
              type="button"
              variant={v.includes(opt.value) ? 'default' : 'outline'}
              disabled={readOnly}
              onClick={() => toggle(opt.value)}
            >
              {opt.label}
            </Button>
          ))}
        </div>
      );
    }
    case 'photo':
      return (
        <p className="text-xs text-fg-muted">Add photos below.</p>
      );
    case 'signature': {
      const hasSignature =
        value?.kind === 'signature' ? Boolean(value.value?.attachmentId) : false;
      if (question.collectPrintedName && onSignatureCapture) {
        return (
          <SignatureField
            printedName={signaturePrintedName}
            onPrintedNameChange={(name) => onPrintedNameChange?.(name)}
            readOnly={readOnly}
            onCapture={onSignatureCapture}
            hasSignature={hasSignature}
            uploading={signatureUploading ?? false}
          />
        );
      }
      return (
        <p className="text-xs text-fg-muted">
          Capture a signature using the field above when printed name is enabled.
        </p>
      );
    }
    default:
      return null;
  }
}

function PhotoStrip({
  inspectionId,
  questionId,
  attachmentIds,
  onChange,
  readOnly,
}: {
  inspectionId: string;
  questionId: string;
  attachmentIds: string[];
  onChange: (next: string[]) => void;
  readOnly: boolean;
}) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState(false);

  async function handleFiles(files: FileList | null) {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      const newIds: string[] = [];
      for (const file of Array.from(files)) {
        const presign = await presignAnswerAttachment(inspectionId, {
          questionId,
          kind: 'inspection_answer_photo',
          filename: file.name,
          contentType: file.type || 'image/jpeg',
          sizeBytes: file.size,
        });
        await fetch(presign.uploadUrl, {
          method: 'PUT',
          body: file,
          headers: presign.headers,
        });
        await confirmAnswerAttachment(inspectionId, questionId, presign.attachmentId);
        newIds.push(presign.attachmentId);
      }
      onChange([...attachmentIds, ...newIds]);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      {attachmentIds.map((aid) => (
        <div
          key={aid}
          className="flex h-16 w-16 items-center justify-center rounded border border-border bg-surface text-[10px] text-fg-muted"
          title={aid}
        >
          {aid.slice(0, 6)}
        </div>
      ))}
      {!readOnly ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
        >
          <Camera className="h-4 w-4" />
          {uploading ? 'Uploading…' : 'Add photo'}
        </Button>
      ) : null}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
      />
    </div>
  );
}

function PdfButton({
  inspectionId,
  pdfLayouts,
  previewOnly = false,
}: {
  inspectionId: string;
  pdfLayouts: InspectionPdfLayout[];
  previewOnly?: boolean;
}) {
  const [polling, setPolling] = useState(false);
  const queryClient = useQueryClient();

  const fetchMut = useMutation({
    mutationFn: (layout: InspectionPdfLayout) =>
      previewOnly
        ? previewInspectionPdf(inspectionId, layout)
        : fetchInspectionPdf(inspectionId, layout),
    onSuccess: (r) => {
      if (r.status === 'ready') {
        window.open(r.url, '_blank');
        setPolling(false);
      } else {
        setPolling(true);
        setTimeout(() => fetchMut.mutate(pdfLayouts[0]!), 4_000);
      }
    },
    onError: (err) => {
      setPolling(false);
      toast.error(err instanceof ApiError ? err.message : 'Could not fetch PDF');
    },
  });

  const retryMut = useMutation({
    mutationFn: () => retryInspectionPdf(inspectionId, pdfLayouts[0]),
    onSuccess: () => {
      toast.success('PDF re-render queued.');
      void queryClient.invalidateQueries({ queryKey: ['inspection', inspectionId] });
      fetchMut.mutate(pdfLayouts[0]!);
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Could not retry'),
  });

  if (pdfLayouts.length > 1) {
    return (
      <div className="flex flex-wrap gap-2">
        {pdfLayouts.map((layout) => (
          <Button
            key={layout}
            onClick={() => fetchMut.mutate(layout)}
            disabled={fetchMut.isPending || polling}
            variant={layout === 'dhs_official' ? 'default' : 'outline'}
          >
            <Download className="h-4 w-4" />
            {polling ? 'Rendering…' : INSPECTION_PDF_LAYOUT_LABELS[layout]}
          </Button>
        ))}
        {!previewOnly ? (
          <Button
            variant="ghost"
            size="icon"
            onClick={() => retryMut.mutate()}
            aria-label="Retry render"
            disabled={retryMut.isPending}
          >
            <RefreshCcw className="h-4 w-4" />
          </Button>
        ) : null}
      </div>
    );
  }

  return (
    <div className="flex gap-2">
      <Button
        onClick={() => fetchMut.mutate(pdfLayouts[0] ?? 'default')}
        disabled={fetchMut.isPending || polling}
      >
        <Download className="h-4 w-4" />
        {polling ? 'Rendering…' : previewOnly ? 'Preview PDF' : 'Download PDF'}
      </Button>
      {!previewOnly ? (
        <Button
          variant="ghost"
          size="icon"
          onClick={() => retryMut.mutate()}
          aria-label="Retry render"
          disabled={retryMut.isPending}
        >
          <RefreshCcw className="h-4 w-4" />
        </Button>
      ) : null}
    </div>
  );
}

function SubmitWithDefectsDialog({
  detail,
  onClose,
  onSubmitAnyway,
}: {
  detail: InspectionDetail;
  onClose: () => void;
  onSubmitAnyway: () => void;
}) {
  const schema = detail.templateVersion.schema as InspectionSchema;
  const labelByQuestionId = new Map<string, { label: string; section: string }>();
  for (const sec of schema.sections) {
    for (const q of sec.questions) {
      if (q.kind === 'section_header') continue;
      labelByQuestionId.set(q.id, { label: q.label, section: sec.title });
    }
  }

  const unresolved = detail.answers.filter(
    (a) => a.isDefect && !a.defectResolvedByWorkOrderId && !a.isDefectAcknowledged,
  );

  return (
    <>
      <Dialog open onOpenChange={(open) => !open && onClose()}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Deficiencies found</DialogTitle>
            <DialogDescription>
              This inspection has {unresolved.length} open deficiency
              {unresolved.length === 1 ? '' : 'ies'}. Create work orders now, or submit and handle
              them afterward.
            </DialogDescription>
          </DialogHeader>
          <ul className="max-h-48 space-y-2 overflow-y-auto text-sm">
            {unresolved.slice(0, 6).map((d) => (
              <li key={d.id} className="rounded border border-border px-3 py-2">
                {labelByQuestionId.get(d.questionId)?.label ?? d.questionId}
              </li>
            ))}
            {unresolved.length > 6 ? (
              <li className="text-xs text-fg-muted">+ {unresolved.length - 6} more</li>
            ) : null}
          </ul>
          <DialogFooter className="gap-2 sm:justify-between">
            <Button variant="ghost" onClick={onClose}>
              Continue editing
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={onSubmitAnyway}>
                Submit anyway
              </Button>
              <CreateWorkOrdersFromDefectsButton
                detail={detail}
                defects={unresolved}
                labelByQuestionId={labelByQuestionId}
                onDone={onClose}
              />
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function CreateWorkOrdersFromDefectsButton({
  detail,
  defects,
  labelByQuestionId,
  onDone,
}: {
  detail: InspectionDetail;
  defects: InspectionAnswerSummary[];
  labelByQuestionId: Map<string, { label: string; section: string }>;
  onDone: () => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button onClick={() => setOpen(true)}>Create work orders</Button>
      {open ? (
        <CreateWorkOrdersDialog
          inspectionId={detail.inspection.id}
          defects={defects}
          labelByQuestionId={labelByQuestionId}
          onClose={() => {
            setOpen(false);
            onDone();
          }}
        />
      ) : null}
    </>
  );
}

function DefectReview({ detail }: { detail: InspectionDetail }) {
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);

  const defects = detail.answers.filter((a) => a.isDefect);
  const unresolved = defects.filter(
    (d) => !d.defectResolvedByWorkOrderId && !d.isDefectAcknowledged,
  );

  const acknowledgeMut = useMutation({
    mutationFn: (questionId: string) =>
      acknowledgeInspectionDefect(detail.inspection.id, questionId),
    onSuccess: () => {
      toast.success('Defect acknowledged.');
      void queryClient.invalidateQueries({ queryKey: ['inspection', detail.inspection.id] });
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Could not acknowledge'),
  });

  const schema = detail.templateVersion.schema as InspectionSchema;
  const labelByQuestionId = new Map<string, { label: string; section: string }>();
  for (const sec of schema.sections) {
    for (const q of sec.questions) {
      if (q.kind === 'section_header') continue;
      labelByQuestionId.set(q.id, {
        label: (q as { label: string }).label,
        section: sec.title,
      });
    }
  }

  if (defects.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Defects</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-fg-muted">No defects flagged. Nice job.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-2">
          <CardTitle className="text-base">
            Defects ({defects.length})
            {unresolved.length > 0 ? (
              <Badge variant="danger" className="ml-2">
                {unresolved.length} unresolved
              </Badge>
            ) : null}
          </CardTitle>
          {unresolved.length > 0 ? (
            <Button onClick={() => setCreateOpen(true)}>
              <FileText className="h-4 w-4" />
              Create work orders
            </Button>
          ) : null}
        </CardHeader>
        <CardContent>
          <ul className="divide-y divide-border">
            {defects.map((d) => {
              const meta = labelByQuestionId.get(d.questionId);
              return (
                <li key={d.id} className="flex items-start justify-between gap-3 py-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">{meta?.label ?? d.questionId}</span>
                      {d.defectResolvedByWorkOrderId ? (
                        <Badge variant="success">WO created</Badge>
                      ) : d.isDefectAcknowledged ? (
                        <Badge variant="default">Acknowledged</Badge>
                      ) : (
                        <Badge variant="danger">Open</Badge>
                      )}
                    </div>
                    <div className="text-xs text-fg-muted">{meta?.section}</div>
                    {d.notes ? (
                      <div className="mt-1 text-xs text-fg-secondary">{d.notes}</div>
                    ) : null}
                  </div>
                  {!d.defectResolvedByWorkOrderId && !d.isDefectAcknowledged ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => acknowledgeMut.mutate(d.questionId)}
                      disabled={acknowledgeMut.isPending}
                    >
                      Acknowledge
                    </Button>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </CardContent>
      </Card>

      {createOpen ? (
        <CreateWorkOrdersDialog
          inspectionId={detail.inspection.id}
          defects={unresolved}
          labelByQuestionId={labelByQuestionId}
          onClose={() => setCreateOpen(false)}
        />
      ) : null}
    </>
  );
}

function CreateWorkOrdersDialog({
  inspectionId,
  defects,
  labelByQuestionId,
  onClose,
}: {
  inspectionId: string;
  defects: InspectionAnswerSummary[];
  labelByQuestionId: Map<string, { label: string; section: string }>;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const titleId = useId();
  const [selected, setSelected] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(defects.map((d) => [d.questionId, true])),
  );
  const [overrides, setOverrides] = useState<Record<string, { notes: string; severity: 'low' | 'medium' | 'high' | 'critical' }>>(
    () =>
      Object.fromEntries(
        defects.map((d) => [
          d.questionId,
          { notes: d.notes ?? '', severity: 'medium' as const },
        ]),
      ),
  );

  const mut = useMutation({
    mutationFn: () =>
      createWorkOrdersFromDefects(inspectionId, {
        defects: defects
          .filter((d) => selected[d.questionId])
          .map((d) => ({
            questionId: d.questionId,
            severity: overrides[d.questionId]?.severity ?? 'medium',
            additionalNotes: overrides[d.questionId]?.notes?.trim() || null,
            assignedTeamId: null,
            assignedUserId: null,
            dueAt: null,
          })),
      }),
    onSuccess: (r) => {
      toast.success(`${r.created.length} work order${r.created.length === 1 ? '' : 's'} created.`);
      void queryClient.invalidateQueries({ queryKey: ['inspection', inspectionId] });
      onClose();
    },
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.message : 'Could not create work orders'),
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Create work orders from defects</DialogTitle>
          <DialogDescription>
            Each selected defect becomes its own work order. Photos attached to the answer are
            re-linked to the new work order.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 max-h-96 overflow-y-auto">
          {defects.map((d) => {
            const meta = labelByQuestionId.get(d.questionId);
            return (
              <div key={d.questionId} className="rounded border border-border p-3">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={selected[d.questionId] ?? false}
                    onChange={(e) =>
                      setSelected({ ...selected, [d.questionId]: e.target.checked })
                    }
                  />
                  <span className="font-medium">{meta?.label ?? d.questionId}</span>
                  <span className="text-xs text-fg-muted">{meta?.section}</span>
                </label>
                {selected[d.questionId] ? (
                  <div className="mt-2 grid gap-2 sm:grid-cols-[2fr,1fr]">
                    <div className="grid gap-1">
                      <Label htmlFor={`${titleId}-${d.questionId}`} className="text-xs">
                        Additional notes
                      </Label>
                      <Input
                        id={`${titleId}-${d.questionId}`}
                        value={overrides[d.questionId]?.notes ?? ''}
                        onChange={(e) =>
                          setOverrides({
                            ...overrides,
                            [d.questionId]: {
                              ...overrides[d.questionId]!,
                              notes: e.target.value,
                            },
                          })
                        }
                      />
                    </div>
                    <div className="grid gap-1">
                      <Label className="text-xs">Severity</Label>
                      <select
                        className="rounded border border-border bg-surface px-3 py-2 text-sm"
                        value={overrides[d.questionId]?.severity ?? 'medium'}
                        onChange={(e) =>
                          setOverrides({
                            ...overrides,
                            [d.questionId]: {
                              ...overrides[d.questionId]!,
                              severity: e.target.value as
                                | 'low'
                                | 'medium'
                                | 'high'
                                | 'critical',
                            },
                          })
                        }
                      >
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                        <option value="critical">Critical</option>
                      </select>
                    </div>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => mut.mutate()} disabled={mut.isPending}>
            {mut.isPending ? 'Creating…' : 'Create work orders'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
